import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, Check, Smartphone, Wallet, FileText, ChevronDown } from "lucide-react";
import { SITE, waUrl } from "@/lib/site";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  legalJustification,
  legalJustificationTitle,
  legalJustificationSummary,
} from "@/lib/legalContent";

const includes = [
  "Tradução juramentada por documento, lauda ou assinatura",
  "Descontos por volume e demanda contínua",
  "Condições especiais para empresas e contratos",
  "Tabelas dedicadas para eventos e interpretação",
];

const paymentMethods = [
  {
    icon: Smartphone,
    country: "Colombia",
    flag: "🇨🇴",
    method: "NEQUI",
    note: "Pago desde Colombia en pesos colombianos (COP).",
  },
  {
    icon: Wallet,
    country: "Brasil",
    flag: "🇧🇷",
    method: "Pix",
    note: "Pagamento instantâneo no Brasil em reais (BRL).",
  },
];

const Pricing = () => {
  return (
    <section id="valores" data-testid="pricing-section" className="py-24 md:py-32 bg-[#FAFAFA]">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        {/* Pricing card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9 }}
          className="grid lg:grid-cols-12 bg-white border border-[#E2E8F0]"
        >
          {/* Left navy panel */}
          <div className="lg:col-span-5 bg-[#0A1128] text-white p-10 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 border border-[#C5A059]/30 -translate-y-12 translate-x-12 rounded-full pointer-events-none" />
            <p className="overline mb-5 text-[#C5A059]"><span className="gold-rule mr-3 align-middle bg-[#C5A059]" />Valores</p>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">
              Investimento <span className="italic">transparente</span>.
            </h2>
            <div className="mt-10 mb-6 flex items-baseline gap-2">
              <span className="text-sm text-white/60">a partir de</span>
            </div>
            <div className="flex items-baseline gap-3" data-testid="pricing-amount">
              <span className="font-serif-display text-6xl md:text-7xl text-[#C5A059]">
                {SITE.priceFrom}
              </span>
              <span className="text-sm text-white/60">/ documento</span>
            </div>
            <p className="mt-6 text-sm text-white/70 leading-relaxed max-w-xs">
              Valores a partir de {SITE.priceFrom} por documento, lauda ou assinatura.
              Cada projeto recebe orçamento personalizado conforme volume e urgência.
            </p>
          </div>

          {/* Right white panel */}
          <div className="lg:col-span-7 p-10 md:p-12">
            <h3 className="font-serif-display text-2xl text-[#0A1128] mb-6">
              O que está incluído
            </h3>
            <ul className="space-y-4 mb-10">
              {includes.map((it, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`pricing-include-${i}`}>
                  <Check size={18} className="text-[#C5A059] flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-sm md:text-base text-[#0A1128]">{it}</span>
                </li>
              ))}
            </ul>

            <a
              href={waUrl("Olá, gostaria de receber um orçamento personalizado para tradução juramentada.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pricing-cta-btn"
              className="inline-flex items-center gap-3 bg-[#25D366] text-white px-7 py-4 rounded-sm hover:bg-[#1DA851] transition-all hover:-translate-y-0.5"
            >
              <MessageCircle size={18} />
              <span className="font-medium tracking-wide">Receber Orçamento no WhatsApp</span>
            </a>
            <p className="mt-4 text-xs text-[#475569]">
              Resposta em até 1 hora útil · sem compromisso
            </p>
          </div>
        </motion.div>

        {/* Payment methods */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-16"
          data-testid="payment-methods"
        >
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="overline mb-4"><span className="gold-rule mr-3 align-middle" />Formas de pagamento</p>
            <h3 className="font-serif-display text-2xl md:text-3xl tracking-tight leading-tight text-[#0A1128]">
              Pagamento binacional, <span className="italic">prático</span> e seguro.
            </h3>
            <p className="mt-4 text-sm md:text-base text-[#475569]">
              Os pagamentos podem ser realizados na Colômbia via <span className="text-[#0A1128] font-medium">NEQUI</span> ou no Brasil via <span className="text-[#0A1128] font-medium">Pix</span>.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {paymentMethods.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={i}
                  data-testid={`payment-${p.method.toLowerCase()}`}
                  className="bg-white border border-[#E2E8F0] hover:border-[#C5A059] transition-colors duration-300 p-7 flex items-start gap-5"
                >
                  <div className="flex-shrink-0 w-14 h-14 rounded-sm bg-[#FAFAFA] border border-[#E2E8F0] flex items-center justify-center">
                    <Icon size={24} strokeWidth={1.5} className="text-[#C5A059]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl" aria-hidden>{p.flag}</span>
                      <p className="text-[11px] tracking-[0.2em] uppercase text-[#475569]">
                        {p.country}
                      </p>
                    </div>
                    <p className="font-serif-display text-2xl text-[#0A1128] leading-tight">
                      {p.method}
                    </p>
                    <p className="text-sm text-[#475569] mt-2 leading-relaxed">{p.note}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Legal justification accordion */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-12"
          data-testid="legal-justification"
        >
          <Accordion type="single" collapsible className="border border-[#E2E8F0] bg-white">
            <AccordionItem value="legal" className="border-0">
              <AccordionTrigger
                data-testid="legal-trigger"
                className="group px-6 md:px-8 py-6 hover:no-underline hover:bg-[#FAFAFA] transition-colors text-left [&[data-state=open]>svg]:rotate-180"
              >
                <div className="flex items-start gap-4 flex-1 pr-4">
                  <div className="flex-shrink-0 w-11 h-11 rounded-sm bg-[#0A1128] text-[#C5A059] flex items-center justify-center">
                    <FileText size={18} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] tracking-[0.25em] uppercase text-[#C5A059] font-semibold mb-1">
                      Constancia legal
                    </p>
                    <p className="font-serif-display text-base md:text-lg text-[#0A1128] leading-snug">
                      {legalJustificationTitle}
                    </p>
                    <p className="text-xs md:text-sm text-[#475569] mt-1.5 leading-relaxed hidden md:block">
                      {legalJustificationSummary}
                    </p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 md:px-8 pb-8">
                <div className="pt-2 border-t border-[#E2E8F0]">
                  <p className="text-xs md:text-sm text-[#475569] mt-5 mb-8 leading-relaxed md:hidden">
                    {legalJustificationSummary}
                  </p>
                  <div className="space-y-8 max-w-3xl">
                    {legalJustification.map((block, i) => (
                      <div key={i} data-testid={`legal-block-${i}`}>
                        <h4 className="font-serif-display text-lg md:text-xl text-[#0A1128] mb-3 leading-snug">
                          {block.heading}
                        </h4>
                        <div className="space-y-3">
                          {block.body.map((p, j) => (
                            <p
                              key={j}
                              className="text-sm md:text-[15px] text-[#475569] leading-relaxed"
                            >
                              {p}
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-10 pt-6 border-t border-[#E2E8F0] text-xs text-[#475569] italic">
                    Documento informativo. La necesidad concreta de traducción oficial y apostilla
                    debe evaluarse según la naturaleza de cada documento y el procedimiento
                    específico ante la autoridad colombiana receptora.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
